import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

type ThemeSetting = "light" | "dark" | "system";

interface ThemeContextValue {
  theme: ThemeSetting;
  setTheme: (t: ThemeSetting) => void;
  /** The resolved theme actually applied (never "system") */
  resolved: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "system",
  setTheme: () => {},
  resolved: "light",
});

const STORAGE_KEY = "theme";
const META_COLORS: Record<"light" | "dark", string> = {
  light: "#f4ede3",
  dark: "#1c1814",
};

function resolveTheme(setting: ThemeSetting): "light" | "dark" {
  if (setting !== "system") return setting;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark") {
  document.documentElement.setAttribute("data-theme", resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", META_COLORS[resolved]);
}

function readStored(): ThemeSetting {
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeSetting>(readStored);
  const [resolved, setResolved] = useState<"light" | "dark">(() => resolveTheme(readStored()));

  const setTheme = useCallback((t: ThemeSetting) => {
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
    const r = resolveTheme(t);
    setResolved(r);
    applyTheme(r);
  }, []);

  // Apply on mount
  useEffect(() => {
    applyTheme(resolved);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount only

  // Listen for OS preference changes when set to "system"
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const r = resolveTheme("system");
      setResolved(r);
      applyTheme(r);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolved }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
